import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Shield,
  Loader2,
  AlertCircle,
  Lock,
  UserCog,
  Users,
  ArrowLeft,
  Handshake
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [error, setError] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [loginType, setLoginType] = useState(null); // null = selection screen, 'admin', 'employee', or 'associate'
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Check if already authenticated using localStorage token
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = () => {
      // Check for existing token based on login type
      const adminToken = localStorage.getItem('admin_token');
      const employeeToken = localStorage.getItem('employee_token');
      
      // If we're on selection screen or associate type, skip auto-login check
      if (!loginType || loginType === 'associate') {
        if (isMounted) setCheckingAuth(false);
        return;
      }
      
      const token = loginType === 'admin' ? adminToken : employeeToken;
      
      if (!token) {
        if (isMounted) setCheckingAuth(false);
        return;
      }

      // Use XMLHttpRequest instead of fetch to avoid postMessage issues
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_URL}/api/auth/me`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      xhr.onload = function() {
        if (!isMounted) return;
        
        if (xhr.status === 200) {
          try {
            const user = JSON.parse(xhr.responseText);
            // Check if the role matches the login type
            const userRole = user.role || localStorage.getItem('user_role');
            if ((loginType === 'admin' && userRole === 'advisor') || 
                (loginType === 'employee' && userRole === 'employee')) {
              navigate('/advisor/dashboard', { state: { user } });
            } else {
              // Role mismatch, clear and show login
              if (loginType === 'admin') {
                localStorage.removeItem('admin_token');
              } else {
                localStorage.removeItem('employee_token');
              }
              setCheckingAuth(false);
            }
          } catch {
            if (loginType === 'admin') {
              localStorage.removeItem('admin_token');
            } else {
              localStorage.removeItem('employee_token');
            }
            setCheckingAuth(false);
          }
        } else {
          if (loginType === 'admin') {
            localStorage.removeItem('admin_token');
          } else {
            localStorage.removeItem('employee_token');
          }
          setCheckingAuth(false);
        }
      };
      
      xhr.onerror = function() {
        if (!isMounted) return;
        if (loginType === 'admin') {
          localStorage.removeItem('admin_token');
        } else {
          localStorage.removeItem('employee_token');
        }
        setCheckingAuth(false);
      };
      
      xhr.send();
    };
    
    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, [navigate, loginType]);

  const handlePinChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError("");

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits entered
    if (value && index === 3 && newPin.every(d => d !== "")) {
      handleLogin(newPin.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d+$/.test(pastedData)) {
      const newPin = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
      setPin(newPin);
      if (newPin.every(d => d !== "")) {
        handleLogin(newPin.join(""));
      }
    }
  };

  const handleLogin = (pinCode) => {
    setLoading(true);
    setError("");

    // Admin/Employee login
    const expectedRole = loginType === 'admin' ? 'advisor' : 'employee';

    // Use XMLHttpRequest to avoid fetch/postMessage issues
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/auth/pin-login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      if (xhr.status === 200) {
        try {
          const data = JSON.parse(xhr.responseText);
          
          // Check if the returned role matches what user selected
          if (data.role !== expectedRole) {
            setError(loginType === 'admin' 
              ? "This PIN is for Employee access. Please use Employee Login." 
              : "This PIN is for Admin access. Please use Admin Login.");
            setPin(["", "", "", ""]);
            inputRefs[0].current?.focus();
            setLoading(false);
            return;
          }
          
          // Store token based on login type (separate storage keys)
          if (data.token) {
            if (loginType === 'admin') {
              localStorage.setItem('admin_token', data.token);
              localStorage.setItem('advisor_token', data.token); // Keep for backward compatibility
            } else {
              localStorage.setItem('employee_token', data.token);
              localStorage.setItem('advisor_token', data.token); // Keep for backward compatibility
            }
          }
          
          // Store user role
          if (data.role) {
            localStorage.setItem('user_role', data.role);
          }
          
          // Navigate to dashboard
          navigate('/advisor/dashboard', { state: { user: data.user, role: data.role } });
        } catch {
          setError("Invalid response from server");
          setPin(["", "", "", ""]);
          inputRefs[0].current?.focus();
          setLoading(false);
        }
      } else {
        let errorMsg = "Invalid PIN";
        try {
          const data = JSON.parse(xhr.responseText);
          errorMsg = data.detail || errorMsg;
        } catch {
          // Use default error
        }
        setError(errorMsg);
        setPin(["", "", "", ""]);
        inputRefs[0].current?.focus();
        setLoading(false);
      }
    };
    
    xhr.onerror = function() {
      setError("Unable to connect to server. Please try again.");
      setPin(["", "", "", ""]);
      inputRefs[0].current?.focus();
      setLoading(false);
    };
    
    xhr.send(JSON.stringify({ pin: pinCode }));
  };

  const selectLoginType = (type) => {
    // Clear any existing session for this type
    if (type === 'admin') {
      localStorage.removeItem('admin_token');
    } else if (type === 'employee') {
      localStorage.removeItem('employee_token');
    }
    setLoginType(type);
    setPin(["", "", "", ""]);
    setError("");
    // Focus first input after state update
    setTimeout(() => {
      inputRefs[0].current?.focus();
    }, 100);
  };

  const goBackToSelection = () => {
    setLoginType(null);
    setPin(["", "", "", ""]);
    setError("");
  };

  if (checkingAuth && loginType) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#E7EDF6] flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#A87608] animate-spin" />
      </div>
    );
  }

  // Role Selection Screen
  if (!loginType) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <img 
              src="/kavachbox-logo-light.png" 
              alt="Kavach Box" 
              className="h-28 sm:h-32 mx-auto mb-2 object-contain"
             
              data-testid="login-logo"
            />
            <h1 className="text-xl font-bold text-[#1B2A4A]">Admin Portal</h1>
            <p className="text-[#3A4557] mt-1 text-sm">Kavach Box CRM · A part of Shree Finserv Group</p>
          </div>

          {/* Login Options */}
          <Card className="bg-white/80 border-[#C9D4E6]/50 backdrop-blur-md">
            <CardContent className="p-6">
              <div className="space-y-4">
                <p className="text-center text-[#2E3B57] mb-6">
                  Select your login type
                </p>

                {/* Admin Login Option */}
                <button
                  onClick={() => selectLoginType('admin')}
                  className="w-full p-4 bg-gradient-to-r from-[#D4A017]/15 to-[#D9A82C]/10 border-2 border-[#D4A017]/40 rounded-xl hover:border-[#D4A017] hover:bg-[#D4A017]/20 transition-all group"
                  data-testid="admin-login-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-[#D4A017]/15 flex items-center justify-center group-hover:bg-[#D4A017]/25 transition-colors">
                      <UserCog className="h-6 w-6 text-[#A87608]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-[#1B2A4A] font-semibold text-lg">Admin Login</h3>
                      <p className="text-[#3A4557] text-sm">Full access to CRM features</p>
                    </div>
                  </div>
                </button>

                {/* Employee Login Option */}
                <button
                  onClick={() => selectLoginType('employee')}
                  className="w-full p-4 bg-gradient-to-r from-[#EDF1F8]/40 to-[#DCE4F2]/30 border-2 border-[#B8C3DA]/20 rounded-xl hover:border-[#B8C3DA]/40 hover:bg-[#EDF1F8]/60 transition-all group"
                  data-testid="employee-login-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-[#EDF1F8]/60 flex items-center justify-center group-hover:bg-[#EDF1F8] transition-colors">
                      <Users className="h-6 w-6 text-[#2E3B57]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-[#1B2A4A] font-semibold text-lg">Employee Login</h3>
                      <p className="text-[#3A4557] text-sm">Task management & operations</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-[#C9D4E6] text-center">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/")}
                  className="text-[#3A4557] hover:text-[#1B2A4A]"
                >
                  ← Back to Website
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-[#3A4557] text-sm mt-6">
            Prashant K Uunadkat • Beshak Verified Advisor
          </p>
        </div>
      </div>
    );
  }

  // PIN Entry Screen
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#E7EDF6] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-4 ${
            loginType === 'admin' 
              ? 'bg-gradient-to-r from-[#D4A017] to-[#D9A82C]' 
              : loginType === 'associate'
              ? 'bg-gradient-to-r from-purple-400 to-purple-500'
              : 'bg-gradient-to-r from-[#EDF1F8] to-[#DCE4F2] border border-[#B8C3DA]/20'
          }`}>
            {loginType === 'admin' ? (
              <UserCog className="h-8 w-8 text-[#0D1A3A]" />
            ) : loginType === 'associate' ? (
              <Handshake className="h-8 w-8 text-[#0D1A3A]" />
            ) : (
              <Users className="h-8 w-8 text-[#2E3B57]" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-[#1B2A4A]">
            {loginType === 'admin' ? 'Admin Login' : loginType === 'associate' ? 'Associate Login' : 'Employee Login'}
          </h1>
          <p className="text-[#3A4557] mt-1">
            {loginType === 'admin' ? 'Full CRM Access' : loginType === 'associate' ? 'Referral & Revenue Tracking' : 'Operations Access'}
          </p>
        </div>

        {/* Login Card */}
        <Card className="bg-[#EDF1F8]/50 border-[#C9D4E6] backdrop-blur-md">
          <CardContent className="p-6">
            <div className="space-y-6">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="text-center">
                <div className={`inline-flex items-center justify-center h-12 w-12 rounded-full mb-4 ${
                  loginType === 'admin' ? 'bg-[#D4A017]/15' : loginType === 'associate' ? 'bg-purple-500/15' : 'bg-[#EDF1F8]'
                }`}>
                  <Lock className={`h-6 w-6 ${loginType === 'admin' ? 'text-[#A87608]' : loginType === 'associate' ? 'text-purple-600' : 'text-[#2E3B57]'}`} />
                </div>
                <p className="text-[#2E3B57] mb-6">
                  Enter your 4-digit {loginType === 'admin' ? 'Admin' : loginType === 'associate' ? 'Associate' : 'Employee'} PIN
                </p>
                
                {/* PIN Input */}
                <div className="flex justify-center gap-3 mb-6">
                  {pin.map((digit, index) => (
                    <input
                      key={index}
                      ref={inputRefs[index]}
                      type="password"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      disabled={loading}
                      className={`w-14 h-14 text-center text-2xl font-bold bg-white border-2 border-[#C9D4E6] rounded-xl text-[#1B2A4A] focus:outline-none transition-colors disabled:opacity-50 ${
                        loginType === 'admin' ? 'focus:border-[#D4A017]' : loginType === 'associate' ? 'focus:border-purple-400' : 'focus:border-[#B8C3DA]'
                      }`}
                      data-testid={`pin-input-${index}`}
                    />
                  ))}
                </div>

                {loading && (
                  <div className={`flex items-center justify-center gap-2 ${
                    loginType === 'admin' ? 'text-[#A87608]' : loginType === 'associate' ? 'text-purple-600' : 'text-[#2E3B57]'
                  }`}>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Verifying...</span>
                  </div>
                )}
              </div>

              <div className="text-center text-sm text-[#3A4557]">
                <p>
                  {loginType === 'admin' 
                    ? 'Admin PIN provides full access to all features' 
                    : loginType === 'associate'
                    ? 'Associate PIN provides access to your referral dashboard'
                    : 'Employee PIN provides access to task management'}
                </p>
              </div>

              {/* Back to selection */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={goBackToSelection}
                  className={`text-sm hover:underline ${
                    loginType === 'admin' ? 'text-[#A87608]' : loginType === 'associate' ? 'text-purple-600' : 'text-[#2E3B57]'
                  }`}
                >
                  <ArrowLeft className="h-4 w-4 inline mr-1" />
                  Choose different login type
                </button>
              </div>

              {/* Clear Session Link - for troubleshooting */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => {
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.reload();
                  }}
                  className="text-xs text-[#3A4557] hover:text-[#A87608] underline"
                >
                  Having trouble? Clear session & retry
                </button>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#C9D4E6] text-center">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                ← Back to Website
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-[#3A4557] text-sm mt-6">
          Prashant K Uunadkat • Beshak Verified Advisor
        </p>
      </div>
    </div>
  );
}
